/**
 * Chat attachment validation — guards files a user drops/attaches into an enquiry
 * BEFORE upload: count cap, per-file size cap, an extension+MIME allowlist, and a
 * mismatch check (a disguised extension is rejected). Pure + framework-free so the
 * composer and tests share one source of truth.
 */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10 MB per file
export const MAX_ATTACHMENTS = 5;

export type AttachmentKind = 'document' | 'image' | 'data';

interface AllowEntry { exts: string[]; mimes: string[]; kind: AttachmentKind }

// Supported reference types for research / ETL work + screenshots.
const ALLOWLIST: AllowEntry[] = [
  { exts: ['.pdf'], mimes: ['application/pdf'], kind: 'document' },
  { exts: ['.doc', '.docx'], mimes: ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'], kind: 'document' },
  { exts: ['.pptx'], mimes: ['application/vnd.openxmlformats-officedocument.presentationml.presentation'], kind: 'document' },
  { exts: ['.txt', '.md', '.markdown', '.log'], mimes: ['text/plain', 'text/markdown', 'text/x-log'], kind: 'document' },
  { exts: ['.html', '.htm'], mimes: ['text/html'], kind: 'document' },
  { exts: ['.csv', '.tsv'], mimes: ['text/csv', 'text/tab-separated-values', 'application/csv', 'application/vnd.ms-excel', 'text/plain'], kind: 'data' },
  { exts: ['.xlsx'], mimes: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'], kind: 'data' },
  { exts: ['.json'], mimes: ['application/json', 'text/json', 'text/plain'], kind: 'data' },
  { exts: ['.png', '.jpg', '.jpeg', '.webp'], mimes: ['image/png', 'image/jpeg', 'image/webp'], kind: 'image' },
];

const ALL_MIMES = new Set(ALLOWLIST.flatMap((a) => a.mimes));

export interface AcceptedAttachment { file: File; kind: AttachmentKind }
export interface RejectedAttachment { name: string; reason: string }
export interface AttachmentValidation { accepted: AcceptedAttachment[]; rejected: RejectedAttachment[] }

function extOf(name: string): string {
  const i = (name || '').lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
}

/**
 * Classify a file by the allowlist, or null if unsupported. Requires an allowed extension AND — when the
 * browser reports a SPECIFIC MIME — that it's an allowed one, so a `malware.exe` renamed `photo.png` (mime
 * application/x-msdownload) is rejected. The GENERIC `application/octet-stream` is allowed through, because
 * many OSes/browsers report it for legitimate .csv/.txt/.pdf uploads — rejecting those was breaking data
 * analysis. We never execute uploads (only extract text / store), so a generic MIME is safe here.
 */
export function classifyAttachment(file: File): AttachmentKind | null {
  const entry = ALLOWLIST.find((a) => a.exts.includes(extOf(file.name)));
  if (!entry) return null;
  const mime = (file.type || '').toLowerCase();
  if (mime && mime !== 'application/octet-stream' && !ALL_MIMES.has(mime)) return null; // real extension/MIME mismatch
  return entry.kind;
}

/** Human-readable size, e.g. "2.4 MB". */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${Math.round(v * 10) / 10} ${units[i]}`;
}

/**
 * Validate a batch against caps + allowlist, accounting for files already attached.
 * Returns accepted files (with kind) and rejected ones (with a friendly reason).
 */
export function validateAttachments(files: File[], existingCount = 0): AttachmentValidation {
  const accepted: AcceptedAttachment[] = [];
  const rejected: RejectedAttachment[] = [];
  for (const file of files) {
    if (existingCount + accepted.length >= MAX_ATTACHMENTS) {
      rejected.push({ name: file.name, reason: `Up to ${MAX_ATTACHMENTS} files per message` });
      continue;
    }
    const kind = classifyAttachment(file);
    if (!kind) { rejected.push({ name: file.name, reason: 'Unsupported format' }); continue; }
    if (file.size === 0) { rejected.push({ name: file.name, reason: 'File is empty or corrupt' }); continue; }
    if (file.size > MAX_ATTACHMENT_BYTES) { rejected.push({ name: file.name, reason: 'Too large (max 10 MB)' }); continue; }
    accepted.push({ file, kind });
  }
  return { accepted, rejected };
}
